import type { CampaignCreationCapabilities, SmartCreationDraft } from "@adpilot/shared";
import { useCallback, useEffect, useRef, useState } from "react";

const SHOP_SWITCH_SKELETON_MIN_MS = 500;

interface ShopTransitionOptions {
  shopId: string;
  shopChanging: boolean;
  onShopChange: (shopId: string) => Promise<void>;
  /** Shop of the Plan currently loaded, plus the data the products step needs to render. */
  loadedShopId: string | undefined;
  draft: SmartCreationDraft | null;
  capabilities: CampaignCreationCapabilities | undefined;
}

/**
 * Switching the creation Shop shows a products skeleton for at least
 * SHOP_SWITCH_SKELETON_MIN_MS so the swap never flashes, and until the new Shop's Plan,
 * draft and capabilities have all arrived.
 */
export function useShopTransition({
  shopId,
  shopChanging,
  onShopChange,
  loadedShopId,
  draft,
  capabilities,
}: ShopTransitionOptions) {
  const [pendingShopId, setPendingShopId] = useState<string | null>(null);
  const [shopTransitionMinimumElapsed, setShopTransitionMinimumElapsed] = useState(false);
  const shopTransitionStartedAtRef = useRef(0);
  const shopTransitionTimerRef = useRef<number | null>(null);
  const finishShopTransition = useCallback(() => {
    const elapsed = Date.now() - shopTransitionStartedAtRef.current;
    const remaining = Math.max(0, SHOP_SWITCH_SKELETON_MIN_MS - elapsed);
    if (shopTransitionTimerRef.current != null) {
      window.clearTimeout(shopTransitionTimerRef.current);
    }
    shopTransitionTimerRef.current = window.setTimeout(() => {
      setPendingShopId(null);
      setShopTransitionMinimumElapsed(false);
      shopTransitionStartedAtRef.current = 0;
      shopTransitionTimerRef.current = null;
    }, remaining);
  }, []);
  const selectCreationShop = useCallback(
    async (nextShopId: string) => {
      if (shopTransitionTimerRef.current != null) {
        window.clearTimeout(shopTransitionTimerRef.current);
      }
      shopTransitionStartedAtRef.current = Date.now();
      setPendingShopId(nextShopId);
      setShopTransitionMinimumElapsed(false);
      shopTransitionTimerRef.current = window.setTimeout(() => {
        setShopTransitionMinimumElapsed(true);
        shopTransitionTimerRef.current = null;
      }, SHOP_SWITCH_SKELETON_MIN_MS);
      try {
        await onShopChange(nextShopId);
      } catch {
        finishShopTransition();
      }
    },
    [finishShopTransition, onShopChange],
  );
  useEffect(
    () => () => {
      if (shopTransitionTimerRef.current != null) {
        window.clearTimeout(shopTransitionTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (
      pendingShopId === shopId &&
      !shopChanging &&
      loadedShopId === shopId &&
      draft &&
      capabilities
    ) {
      finishShopTransition();
    }
  }, [
    capabilities,
    draft,
    finishShopTransition,
    pendingShopId,
    loadedShopId,
    shopChanging,
    shopId,
  ]);

  return { pendingShopId, shopTransitionMinimumElapsed, selectCreationShop };
}
